import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '@/api/services';
import { AccountOperationType, InterfaceResponseEnum } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import naLogo from '@/assets/Nautic Alert Logo.png';

// Validation helpers
const INVALID_CHARS = /[\s/\\'"\t]/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS_ONLY = /^\d+$/;

interface FieldErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
  phone?: string;
  email?: string;
}

function validateFields(
  username: string,
  password: string,
  confirmPassword: string,
  phone: string,
  email: string,
): FieldErrors {
  const errors: FieldErrors = {};

  if (username.length < 4 || username.length > 49) {
    errors.username = 'Username must be 4-49 characters.';
  } else if (INVALID_CHARS.test(username)) {
    errors.username = 'Username cannot contain spaces, slashes, tabs, or quotes.';
  }

  if (password.length < 6 || password.length > 29) {
    errors.password = 'Password must be 6-29 characters.';
  } else if (INVALID_CHARS.test(password)) {
    errors.password = 'Password cannot contain spaces, slashes, tabs, or quotes.';
  }

  if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 10 || digitsOnly.length > 20) {
    errors.phone = 'Phone must be 10-20 digits.';
  } else if (!DIGITS_ONLY.test(digitsOnly)) {
    errors.phone = 'Phone must contain only digits.';
  }

  if (!EMAIL_REGEX.test(email)) {
    errors.email = 'Enter a valid email address.';
  } else if (email.length > 50) {
    errors.email = 'Email must be 50 characters or less.';
  }

  return errors;
}

function mapResponseError(responseEnum: InterfaceResponseEnum | number | undefined): string {
  switch (Number(responseEnum)) {
    case InterfaceResponseEnum.DUPLICATE_ENTRY:
      return 'Username, email, or phone number is already in use.';
    case InterfaceResponseEnum.MISSING_ASSOCIATION:
      return 'Phone number must be registered with a Nautic Alert device first.';
    case InterfaceResponseEnum.PASSWORD_INVALID:
      return 'Password does not meet requirements (6–29 characters, no spaces or special characters).';
    case InterfaceResponseEnum.USER_ID_INVALID:
      return 'Username does not meet requirements (4–49 characters, no spaces or special characters).';
    case InterfaceResponseEnum.CELL_INVALID:
      return 'Invalid phone number format.';
    case InterfaceResponseEnum.EMAIL_INVALID:
      return 'Invalid email address.';
    case InterfaceResponseEnum.VERIFICATION_CODE_INVALID:
      return 'Incorrect verification code. Please try again.';
    case InterfaceResponseEnum.VERIFICATION_LIMIT:
      return 'Maximum verification attempts reached. Please start the registration process over.';
    case InterfaceResponseEnum.TIMEOUT:
      return 'Verification code has expired. Please start the registration process over.';
    case InterfaceResponseEnum.INVALID_PARAMS:
      return 'Missing required information. Please fill in all fields.';
    case InterfaceResponseEnum.NOT_FOUND:
      return 'Account not found.';
    case InterfaceResponseEnum.SERVER_ERROR:
    case InterfaceResponseEnum.DB_ERROR:
    case InterfaceResponseEnum.DB_WRITE_ERROR:
      return 'A server error occurred. Please try again later.';
    case InterfaceResponseEnum.IP_BLACKLISTED:
      return 'Access denied from this network. Please contact support.';
    case InterfaceResponseEnum.BAD_REQUEST:
    case InterfaceResponseEnum.BAD_STATE:
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

export function CreateAccountPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Step 2 fields
  const [verificationCode, setVerificationCode] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);

  // Shared
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const cellDigits = phone.replace(/\D/g, '');

  const handleStep1Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const errors = validateFields(username, password, confirmPassword, phone, email);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);
    try {
      const resp = await authService.accountOperation({
        operationType: AccountOperationType.PRE_CREATE_VERIFY,
        userName: username,
        newPassword: password,
        cellStr: cellDigits,
        emailStr: email,
      });

      // Support both wrapped {responseObj:{httpStatusCode,responseEnum}} and flat {httpStatusCode,responseEnum}
      const flat = resp as unknown as { httpStatusCode: number; responseEnum: number };
      const statusCode = resp.responseObj?.httpStatusCode ?? flat.httpStatusCode ?? 400;
      const enumVal = resp.responseObj?.responseEnum ?? flat.responseEnum;

      if (statusCode === 200) {
        setStep(2);
        setAttemptsRemaining(3);
        setVerificationCode('');
      } else {
        setError(mapResponseError(enumVal));
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (verificationCode.length !== 5 || !/^\d{5}$/.test(verificationCode)) {
      setError('Please enter a 5-digit verification code.');
      return;
    }

    setIsLoading(true);
    try {
      const resp = await authService.accountOperation({
        operationType: AccountOperationType.POST_CREATE_VERIFY,
        userName: username,
        newPassword: password,
        cellStr: cellDigits,
        emailStr: email,
        verificationCode,
      });

      const flat = resp as unknown as { httpStatusCode: number; responseEnum: number };
      const statusCode = resp.responseObj?.httpStatusCode ?? flat.httpStatusCode ?? 400;
      const enumVal = resp.responseObj?.responseEnum ?? flat.responseEnum;

      if (statusCode === 200) {
        setStep(3);
      } else {
        if (Number(enumVal) === InterfaceResponseEnum.VERIFICATION_CODE_INVALID) {
          setAttemptsRemaining((prev) => Math.max(0, prev - 1));
        }
        setError(mapResponseError(enumVal));
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    setStep(1);
    setVerificationCode('');
    setError('');
  };

  const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500 dark:focus:border-teal-500';
  const labelClass =
    'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';
  const fieldErrorClass = 'mt-1 text-xs text-red-600 dark:text-red-400';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-navy-950">
      <div className="w-full max-w-md px-4">
        {/* Logo & Brand */}
        <div className="mb-8 text-center">
          <img src={naLogo} alt="Nautic Alert" className="mx-auto mb-4 h-16 object-contain" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Vessel Monitoring Portal</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-lg dark:border-navy-700 dark:bg-navy-900">
          {/* Step indicator */}
          <div className="mb-6 flex items-center justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                    step >= s
                      ? 'bg-teal-500 text-white'
                      : 'bg-slate-200 text-slate-500 dark:bg-navy-700 dark:text-slate-400'
                  }`}
                >
                  {step > s ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    s
                  )}
                </div>
                {s < 3 && (
                  <div
                    className={`h-0.5 w-8 ${
                      step > s ? 'bg-teal-500' : 'bg-slate-200 dark:bg-navy-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Enter Information */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-4">
              <h2 className="text-center text-lg font-semibold text-slate-800 dark:text-white">
                Enter Information
              </h2>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="ca-username" className={labelClass}>
                  Username
                </label>
                <input
                  id="ca-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={inputClass}
                  placeholder="4-49 characters"
                  autoComplete="username"
                  autoFocus
                />
                {fieldErrors.username && (
                  <p className={fieldErrorClass}>{fieldErrors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="ca-password" className={labelClass}>
                  Password
                </label>
                <input
                  id="ca-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="6-29 characters"
                  autoComplete="new-password"
                />
                {fieldErrors.password && (
                  <p className={fieldErrorClass}>{fieldErrors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="ca-confirm-password" className={labelClass}>
                  Confirm Password
                </label>
                <input
                  id="ca-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
                {fieldErrors.confirmPassword && (
                  <p className={fieldErrorClass}>{fieldErrors.confirmPassword}</p>
                )}
              </div>

              <div>
                <label htmlFor="ca-phone" className={labelClass}>
                  Phone Number
                </label>
                <input
                  id="ca-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass}
                  placeholder="10+ digits, numbers only"
                  autoComplete="tel"
                />
                {fieldErrors.phone && (
                  <p className={fieldErrorClass}>{fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="ca-email" className={labelClass}>
                  Email
                </label>
                <input
                  id="ca-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                {fieldErrors.email && (
                  <p className={fieldErrorClass}>{fieldErrors.email}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                loading={isLoading}
                className="w-full"
              >
                Create Account
              </Button>
            </form>
          )}

          {/* Step 2: Verification Code */}
          {step === 2 && (
            <form onSubmit={handleStep2Submit} className="space-y-4">
              <h2 className="text-center text-lg font-semibold text-slate-800 dark:text-white">
                Enter Verification Code
              </h2>

              <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-400">
                A verification code has been sent to your phone. The code expires in 10 minutes.
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="ca-code" className={labelClass}>
                  Verification Code
                </label>
                <input
                  id="ca-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 5))
                  }
                  className={`${inputClass} text-center text-lg tracking-widest`}
                  placeholder="00000"
                  autoFocus
                />
              </div>

              <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                Attempts remaining:{' '}
                <span className={`font-semibold ${attemptsRemaining <= 1 ? 'text-red-500' : 'text-slate-700 dark:text-slate-300'}`}>
                  {attemptsRemaining} of 3
                </span>
              </p>

              <Button
                type="submit"
                disabled={isLoading || verificationCode.length !== 5}
                loading={isLoading}
                className="w-full"
              >
                Verify
              </Button>

              <button
                type="button"
                onClick={handleResendCode}
                className="w-full text-center text-sm text-teal-600 hover:text-teal-500 dark:text-teal-400 dark:hover:text-teal-300"
              >
                Resend Code
              </button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <svg
                  className="h-7 w-7 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                Account Created Successfully
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Your account has been created. You can now sign in with your credentials.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Sign In
              </Button>
            </div>
          )}

          {/* Back to Sign In */}
          {step !== 3 && (
            <div className="mt-6 border-t border-slate-200 pt-6 text-center dark:border-navy-700">
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
              >
                Back to Sign In
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          &copy; {new Date().getFullYear()} Nautic Alert. All rights reserved.
        </p>
      </div>
    </div>
  );
}
